import gql from "graphql-tag";
import {useCallback} from "react";

import {useGrowthBookForABTest} from "@khan/analytics-growthbook";
import {useGql} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType} from "./__graphql-types__/index.ts";

export const CONFIG_QUERY: GqlOperation<
    AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType["response"],
    AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType["variables"]
> = gqlOp(gql`
    query AiGuideActivityConfigGrowthbookFeaturesAsPromptVariables(
        $threadId: String!
    ) {
        aiGuideActivityConfigByThreadId(threadId: $threadId) {
            growthbookFeaturesAsPromptVariables
        }
    }
`);

export const useGetGrowthbookFeaturesAsPromptVariables = () => {
    const gqlFetch = useGql();
    const growthBook = useGrowthBookForABTest();

    return useCallback(
        async (threadId: string | null | undefined) => {
            if (!growthBook || !threadId) {
                return {};
            }

            let aiGuideActivityConfig: AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType["response"];
            try {
                aiGuideActivityConfig = await gqlFetch(CONFIG_QUERY, {
                    variables: {threadId},
                });
            } catch (error) {
                return {};
            }

            const featureKeys =
                aiGuideActivityConfig?.aiGuideActivityConfigByThreadId
                    ?.growthbookFeaturesAsPromptVariables;
            if (!featureKeys) {
                return {};
            }

            // Add threadId context to GrowthBook Attributes.
            growthBook?.setAttributes({
                ...growthBook.getAttributes(),
                threadId,
            });

            const promptVariables: Record<string, string | null> = {};

            for (const featureKey of featureKeys) {
                const rawFeatureValue =
                    growthBook.getFeatureValueAndEnrollInABTest(
                        featureKey,
                        "NONE",
                    );

                // Transform the feature key using kebab-case to camelCase.
                const safeFeatureKey = featureKey.replace(
                    /-([a-z])/g,
                    (_, letter) => letter.toUpperCase(),
                );

                // Be defensive: ensure we always pass a string (or null) to the
                // prompt variables API.
                let stringValue: string | null;
                if (!rawFeatureValue || rawFeatureValue === "NONE") {
                    stringValue = null;
                } else if (typeof rawFeatureValue === "object") {
                    try {
                        stringValue = JSON.stringify(rawFeatureValue);
                    } catch {
                        stringValue = String(rawFeatureValue);
                    }
                } else {
                    stringValue = String(rawFeatureValue);
                }

                promptVariables[safeFeatureKey] = stringValue;
            }

            return promptVariables;
        },
        [gqlFetch, growthBook],
    );
};
